<template>
  <div class="quickbi-embed-wrap">
    <div v-if="loadError" class="embed-error">{{ loadError }}</div>
    <embed-component
      v-else-if="embedReady"
      :src="embedSrc"
      :key="embedComponentKey"
      :feature="embedFeature"
      :option="embedOption"
      class="my-embed"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import {
  EmbedComponent,
  EmbedRouteKey,
  parsePath
} from '@quickbi/bi-embed-client-vue2'
import api from '@/api/index'
import { getQuickBiEmbedLocale, appendQuickBiLocaleToUrl } from '@/utils/util'

const QUICKBI_EMBED_ACCOUNT_STORAGE_PREFIX = 'quickbi_embed_account_name'

const QUICKBI_OPEN_ACTION_TYPES = {
  selfServiceAnalysisEmbed: 'QuickBI-selfServiceAnalysis',
  selfServiceDownloadEmbed: 'QuickBI-isSelfServiceDownload',
  xiaoQEmbedReport: 'QuickBI-xiaoQReport'
}

const QUICKBI_PANEL_KEY_ACTION_TYPES = {
  selfServiceAnalysis: 'QuickBI-selfServiceAnalysis',
  selfServiceDownload: 'QuickBI-isSelfServiceDownload',
  xiaoQReport: 'QuickBI-xiaoQReport'
}

/**
 * 构建 QuickBI 嵌入特性配置。
 * 文档：https://help.aliyun.com/zh/quick-bi/user-guide/developing-embedded-examples
 */
function buildEmbedFeature() {
  return {
    [EmbedRouteKey.homeRoot]: {
      header: { show: false }
    },
    [EmbedRouteKey.workspace]: {
      header: { show: false },
      sideNav: { show: false },
      goBack: { show: false },
      switchWorkspace: { show: false },
      breadcrumb: { show: false }
    },
    [EmbedRouteKey.copilotPage]: {
      header: { show: false },
      sideNav: { show: false }
    },
    [EmbedRouteKey.copilotPageQReport]: {
      header: { show: false },
      sideNav: { show: false }
    },
    [EmbedRouteKey.downloadList]: {
      header: { show: false },
      sideNav: { show: false },
      breadcrumb: { show: false }
    }
  }
}

function getEmbedAccountStorageKey(userId, productId) {
  return `${QUICKBI_EMBED_ACCOUNT_STORAGE_PREFIX}_${userId}_${productId}`
}

function preLogoutQuickBI(logoutUrl) {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = logoutUrl
    const done = () => {
      iframe.remove()
      resolve()
    }
    iframe.onload = done
    iframe.onerror = done
    document.body.appendChild(iframe)
    setTimeout(done, 3000)
  })
}

export default Vue.extend({
  name: 'SDK',
  components: {
    EmbedComponent
  },
  props: {
    embedPage: {
      type: String,
      default: ''
    },
    panelKey: {
      type: String,
      default: ''
    }
  },
  data() {
    const defaultPage = this.embedPage || EmbedRouteKey.view

    return {
      initialized: false,
      embedReady: false,
      loadError: '',
      embedConfig: null,
      activeEmbedPage: defaultPage,
      embedSrc: {
        origin: '',
        page: defaultPage,
        search: {}
      },
      embedFeature: buildEmbedFeature(),
      embedOption: {
        log: 'debug'
      }
    }
  },
  computed: {
    embedComponentKey() {
      const accountName = this.embedConfig && this.embedConfig.quickBIAccountName
      return `${this.embedSrc.page}_${accountName || ''}`
    }
  },
  watch: {
    embedPage: {
      immediate: false,
      handler(newPage) {
        if (newPage && newPage !== this.activeEmbedPage) {
          this.activeEmbedPage = newPage
          this.embedSrc = {
            ...this.embedSrc,
            page: newPage,
            search: {}
          }
          this.embedReady = false
          this.initEmbed()
        }
      }
    }
  },
  methods: {
    getCurrentUserId() {
      return this.$store.state.user && this.$store.state.user.id
    },

    async getQuickBIConfig() {
      const userId = this.getCurrentUserId()
      if (!userId) {
        this.loadError = '请先登录门户'
        this.$message.error(this.loadError)
        return false
      }

      try {
        const response = await api.QuickBI.GetQuickBIEmbedConfig(userId)

        if (response && response.isSuccess && response.data) {
          const config = response.data
          this.embedConfig = config

          this.$set(this, 'embedSrc', {
            ...this.embedSrc,
            origin: config.domain,
            search: {
              ...this.embedSrc.search,
              workspaceId: config.workSpaceId,
              loginToken: config.loginToken,
              locale: getQuickBiEmbedLocale()
            }
          })

          return true
        }

        this.loadError = (response && response.message) || '获取 QuickBI 嵌入配置失败'
        this.$message.error(this.loadError)
        return false
      } catch (error) {
        console.error('调用 QuickBI 配置接口出错:', error)
        this.loadError = '获取 QuickBI 嵌入配置失败'
        this.$message.error(this.loadError)
        return false
      }
    },

    async ensureQuickBIAccountSession() {
      const config = this.embedConfig
      const userId = this.getCurrentUserId()
      if (!config || !userId) {
        return
      }

      const storageKey = getEmbedAccountStorageKey(userId, config.productId)
      const lastAccountName = sessionStorage.getItem(storageKey)
      const currentAccountName = config.quickBIAccountName || ''
      const needLogout = lastAccountName && lastAccountName !== currentAccountName

      if (!needLogout) {
        sessionStorage.setItem(storageKey, currentAccountName)
        return
      }

      if (!config.quickBILogoutUrl) {
        this.loadError = 'QuickBI 映射已变更，但未配置跨域登出密钥，无法切换用户。请联系管理员配置后重试，或清除浏览器 Cookie。'
        this.$message.error(this.loadError)
        throw new Error(this.loadError)
      }

      await preLogoutQuickBI(config.quickBILogoutUrl)
      sessionStorage.setItem(storageKey, currentAccountName)
    },

    applyLinkOverride() {
      const urlParams = new URLSearchParams(window.location.search)
      const encodedLink = urlParams.get('link')
      const href = encodedLink ? decodeURIComponent(encodedLink) : ''
      const src = parsePath(href, { origin: [this.embedSrc.origin] })

      if (src && src.page) {
        this.$set(this, 'embedSrc', {
          ...src,
          origin: this.embedSrc.origin,
          search: {
            ...src.search,
            loginToken: this.embedSrc.search.loginToken,
            workspaceId: src.search && src.search.workspaceId
              ? src.search.workspaceId
              : this.embedSrc.search.workspaceId,
            locale: getQuickBiEmbedLocale()
          }
        })
      }
    },

    logOpenAction() {
      const actionType =
        QUICKBI_PANEL_KEY_ACTION_TYPES[this.panelKey] ||
        QUICKBI_OPEN_ACTION_TYPES[this.$route.name]
      if (!actionType) {
        return
      }

      api.EarthLog.Log({
        UserName: this.$store.state.user.userName,
        FullName: this.$store.state.user.fullName,
        ActionType: actionType,
        ClientType: 'PC',
        Content: '',
        CommentContent: ''
      })
    },

    async initEmbed() {
      if (this.initialized && this.embedReady) {
        return
      }

      const loaded = await this.getQuickBIConfig()
      if (!loaded) {
        this.embedReady = false
        return
      }

      try {
        await this.ensureQuickBIAccountSession()
      } catch (error) {
        this.embedReady = false
        return
      }

      this.applyLinkOverride()
      this.embedReady = true
      this.initialized = true
      this.logOpenAction()
    }
  },

  async mounted() {
    await this.initEmbed()
  }
})
</script>

<style scoped>
.quickbi-embed-wrap {
  height: 100%;
}

.my-embed {
  height: 100%;
}

.embed-error {
  padding: 24px;
  color: #c45656;
}
</style>
